"use client";

import { motion, AnimatePresence } from "motion/react";
import { useState, useMemo, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Clock,
  ShieldCheck,
  Tag,
  Info,
  X,
  ChevronLeft,
  ChevronRight,
  Flame,
  Play,
  Ticket,
  Plus,
  Minus,
} from "lucide-react";
import { decodeHtml, formatCurrency, cleanHeaderTags } from "@/_helper/helper";
import { useAppDispatch, useAppSelector } from "@/_hooks/hooks";
import Preloader from "../home/Preloader";
import { ISettings } from "@/store/homeSlice";
import Slider from "react-slick";
import { fetchTicketByURLAPI } from "@/services/ticket.services";
import TicketCard from "./TicketCard";

const RelatedTicketPrevArrow = (props: any) => (
  <button
    onClick={props.onClick}
    className="absolute -left-4 md:-left-12 top-1/2 -translate-y-1/2 z-20 p-3 bg-white hover:bg-orange-500 border border-gray-200 hover:border-orange-500 rounded-full transition-all text-gray-400 hover:text-white shadow-lg group active:scale-95"
    aria-label="Previous slide"
  >
    <ChevronLeft className="w-6 h-6 transition-transform group-hover:-translate-x-0.5" />
  </button>
);

const RelatedTicketNextArrow = (props: any) => (
  <button
    onClick={props.onClick}
    className="absolute -right-4 md:-right-12 top-1/2 -translate-y-1/2 z-20 p-3 bg-white hover:bg-orange-500 border border-gray-200 hover:border-orange-500 rounded-full transition-all text-gray-400 hover:text-white shadow-lg group active:scale-95"
    aria-label="Next slide"
  >
    <ChevronRight className="w-6 h-6 transition-transform group-hover:translate-x-0.5" />
  </button>
);

export default function TicketDetailClient({
  ticketDetails,
}: {
  ticketDetails: any;
}) {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.home.loading);
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const getSlidesToShow = () => {
    if (typeof window === "undefined") return 3;
    const w = window.innerWidth;
    if (w <= 768) return 1;
    if (w <= 1024) return 2;
    return 3;
  };

  const [slidesCount, setSlidesCount] = useState(getSlidesToShow);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    const onResize = () => setSlidesCount(getSlidesToShow());
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [nav1, setNav1] = useState<Slider | null>(null);
  const [nav2, setNav2] = useState<Slider | null>(null);
  const slider1 = useRef<Slider>(null);
  const slider2 = useRef<Slider>(null);
  const [activeImages, setActiveImages] = useState<any[]>([]);
  const [activeTitle, setActiveTitle] = useState<string>("");
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number | null>(
    null,
  );

  const [navVideo1, setNavVideo1] = useState<Slider | null>(null);
  const [navVideo2, setNavVideo2] = useState<Slider | null>(null);
  const sliderVideo1 = useRef<Slider>(null);
  const sliderVideo2 = useRef<Slider>(null);
  const [currentVideoSlide, setCurrentVideoSlide] = useState(0);

  const getYouTubeID = (url: string) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  useEffect(() => {
    dispatch(fetchTicketByURLAPI("sou-viewing-gallery"));
  }, []);

  useEffect(() => {
    if (selectedIndex !== null) {
      setNav1(slider1.current);
      setNav2(slider2.current);
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") setSelectedIndex(null);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "unset";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else if (selectedVideoIndex === null) {
      document.body.style.overflow = "unset";
    }
  }, [selectedIndex, activeImages]);

  useEffect(() => {
    if (selectedVideoIndex !== null) {
      setNavVideo1(sliderVideo1.current);
      setNavVideo2(sliderVideo2.current);
      setCurrentVideoSlide(selectedVideoIndex);
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") setSelectedVideoIndex(null);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "unset";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else if (selectedIndex === null) {
      document.body.style.overflow = "unset";
    }
  }, [selectedVideoIndex]);

  const Settings: Record<string, ISettings> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );

  const PhoneNo = Settings?.["PhoneNo"]?.SettingValue;

  const ticket = ticketDetails;

  if (!ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Ticket Not Found
          </h1>
          <Link
            href="/sou-tickets"
            className="text-orange-600 hover:text-orange-700 font-semibold"
          >
            Browse All Tickets
          </Link>
        </div>
      </div>
    );
  }

  const galleryImages = useMemo(() => {
    const images: any[] = [];
    if (ticket?.SOUTicketImage && Array.isArray(ticket?.SOUTicketImage)) {
      ticket?.SOUTicketImage.forEach((img: any) => {
        if (img.Image) images.push(img.Image);
      });
    }
    return Array.from(new Set(images.filter(Boolean)));
  }, [ticket]);

  // const basePrice = useMemo(() => {
  //   if (ticket?.SOUTicketDetails && ticket?.SOUTicketDetails?.length > 0) {
  //     return ticket?.SOUTicketDetails[0]?.Rate;
  //   }
  //   return 0;
  // }, [ticket]);

  return (
    <>
      {loading && <Preloader />}
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[50vh] overflow-hidden">
          <Image
            src={
              ticket?.BannerImage || ticket?.ImageUpload || "/placeholder.jpg"
            }
            alt={ticket?.Title}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent"></div>

          <div className="absolute inset-0 flex items-end pb-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  {ticket?.IsPopular && (
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                      <Flame className="w-3.5 h-3.5 fill-white animate-pulse" />
                      Highest Selling
                    </span>
                  )}
                </div>
                <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight drop-shadow-lg">
                  {ticket?.Title}
                </h1>
                <div className="flex">
                  {ticket?.LastEntry && (
                    <span className="text-white font-bold flex gap-2 items-center mt-2 backgrop-blur-sm border border-white/20 px-4 py-1 rounded-full ">
                      <Clock className="w-3.5 h-3.5" />
                      Last Entry: {ticket.LastEntry}
                    </span>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-16">
              {/* Overview Section */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="prose prose-lg max-w-none mb-8"
              >
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-1.5 h-8 bg-orange-500 rounded-full"></div>
                  <h2 className="text-3xl font-bold text-gray-900 m-0">
                    Description
                  </h2>
                </div>
                {ticket?.Description ? (
                  <div
                    className="text-lg text-gray-700 leading-relaxed space-y-4 blog-content"
                    dangerouslySetInnerHTML={{
                      __html: cleanHeaderTags(decodeHtml(ticket?.Description)),
                    }}
                  />
                ) : (
                  <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-12 flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <Info className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium text-lg">
                      No detailed description available for this ticket.
                    </p>
                  </div>
                )}
              </motion.section>

              {/* Instructions Section */}
              {ticket?.InstructionsForVisitors && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-amber-50 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-amber-100 mb-8"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <Info className="w-6 h-6 text-amber-600" />
                    <h2 className="text-2xl font-bold text-gray-900 m-0">
                      Visitor Instructions
                    </h2>
                  </div>
                  <div
                    className="text-gray-700 leading-relaxed font-sans prose prose-sm max-w-none prose-ul:list-disc prose-li:mb-2 blog-content"
                    dangerouslySetInnerHTML={{
                      __html: cleanHeaderTags(
                        decodeHtml(ticket?.InstructionsForVisitors),
                      ),
                    }}
                  />
                </motion.section>
              )}

              {/* Photo Gallery Section */}
              {galleryImages?.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-orange-50/30 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-orange-100 mb-8"
                >
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-1.5 h-8 bg-orange-500 rounded-full"></div>
                    <h2 className="text-3xl font-bold text-gray-900 m-0">
                      Photo Gallery
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {galleryImages?.map((image: any, index: number) => (
                      <div
                        key={index}
                        className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer"
                        onClick={() => {
                          setActiveImages(galleryImages);
                          setActiveTitle("Photo Gallery");
                          setSelectedIndex(index);
                        }}
                      >
                        <Image
                          src={image}
                          alt={`${ticket?.Title} ${index + 1}`}
                          fill
                          className="object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                          <span className="text-white font-semibold">
                            View Image
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Video Gallery Section */}
              {ticket?.SOUTicketVideo && ticket?.SOUTicketVideo?.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-orange-50/30 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-orange-100 mb-8"
                >
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-1.5 h-8 bg-orange-500 rounded-full"></div>
                    <h2 className="text-3xl font-bold text-gray-900 m-0">
                      Video Gallery
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {ticket?.SOUTicketVideo?.map(
                      (video: any, index: number) => {
                        const videoID = getYouTubeID(video.URL);
                        if (!videoID) return null;
                        return (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className="relative aspect-video rounded-xl md:rounded-2xl overflow-hidden group cursor-pointer shadow-lg border border-gray-100 hover:border-orange-500/30 transition-all duration-300"
                            onClick={() => {
                              setSelectedVideoIndex(index);
                              setCurrentVideoSlide(index);
                            }}
                          >
                            <Image
                              src={`https://img.youtube.com/vi/${videoID}/maxresdefault.jpg`}
                              alt="Video Thumbnail"
                              fill
                              className="object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                              <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-500 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 group-hover:scale-110 group-hover:bg-orange-600">
                                <Play className="w-6 h-6 md:w-8 md:h-8 text-white fill-white ml-0.5 md:ml-1" />
                              </div>
                            </div>
                            <div className="absolute bottom-3 left-4 md:bottom-4 md:left-6 right-6">
                              <div className="inline-flex items-center gap-2 px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-white text-[10px] md:text-xs font-semibold uppercase tracking-wider border border-white/10">
                                <Play size={10} className="fill-white" />
                                Watch Video
                              </div>
                            </div>
                          </motion.div>
                        );
                      },
                    )}
                  </div>
                </motion.section>
              )}

              {/* FAQ Section */}
              {ticket?.SOUTicketFAQ && ticket?.SOUTicketFAQ?.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-orange-50/30 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-orange-100 mb-8"
                >
                  <div className="flex items-center gap-3 mb-6 md:mb-8">
                    <div className="w-1.5 h-6 md:h-8 bg-orange-500 rounded-full"></div>
                    <h2 className="text-xl md:text-3xl font-bold text-gray-900 m-0">
                      FAQ
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {ticket?.SOUTicketFAQ?.map((faq: any, index: number) => {
                      const isOpen = openFaqIndex === index;
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                            isOpen
                              ? "bg-white border-orange-200 shadow-md ring-1 ring-orange-500/10"
                              : "bg-white/50 border-gray-100 hover:border-orange-200"
                          }`}
                        >
                          <button
                            onClick={() =>
                              setOpenFaqIndex(isOpen ? null : index)
                            }
                            className="w-full px-4 md:px-6 py-4 md:py-5 flex items-center justify-between text-left group"
                          >
                            <div className="flex items-center gap-3 md:gap-4">
                              <div
                                className={`flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-colors font-bold text-xs md:text-sm ${
                                  isOpen
                                    ? "bg-orange-500 text-white"
                                    : "bg-orange-100 text-orange-600 group-hover:bg-orange-500 group-hover:text-white"
                                }`}
                              >
                                {index + 1}
                              </div>
                              <h3
                                className={`text-base md:text-lg font-bold transition-colors ${
                                  isOpen
                                    ? "text-orange-600"
                                    : "text-gray-900 group-hover:text-orange-600"
                                }`}
                              >
                                {faq.Question}
                              </h3>
                            </div>
                            <div
                              className={`p-1.5 rounded-full transition-all duration-300 ${
                                isOpen
                                  ? "bg-orange-100"
                                  : "bg-gray-100 group-hover:bg-orange-100"
                              }`}
                            >
                              {isOpen ? (
                                <Minus className="w-4 h-4 text-orange-600 transition-colors" />
                              ) : (
                                <Plus className="w-4 h-4 text-gray-600 group-hover:text-orange-600 transition-colors" />
                              )}
                            </div>
                          </button>
                          <AnimatePresence>
                            {isOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  duration: 0.3,
                                  ease: "easeInOut",
                                }}
                              >
                                <div className="px-4 md:px-6 pb-5 md:pb-6 pt-0 ml-10 md:ml-12">
                                  <div
                                    className="text-gray-600 leading-relaxed text-sm md:text-base blog-content border-t border-orange-50 border-dashed pt-4"
                                    dangerouslySetInnerHTML={{
                                      __html: cleanHeaderTags(
                                        decodeHtml(faq.Answer),
                                      ),
                                    }}
                                  />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.section>
              )}

              {/* Policies Section */}
              {ticket?.CancellationPolicy && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-red-50 border border-red-100 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-orange-100"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <ShieldCheck className="w-6 h-6 text-red-600" />
                    <h2 className="text-2xl font-bold text-gray-900 m-0">
                      Cancellation Policy
                    </h2>
                  </div>
                  <div
                    className="text-gray-700 leading-relaxed prose prose-sm max-w-none blog-content"
                    dangerouslySetInnerHTML={{
                      __html: cleanHeaderTags(
                        decodeHtml(ticket?.CancellationPolicy),
                      ),
                    }}
                  />
                </motion.section>
              )}
            </div>

            {/* Right Column - Sticky Sidebar with Pricing & Booking */}
            <aside className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="sticky top-28 space-y-6"
              >
                {/* Booking & Pricing Card */}
                <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/5 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-150 duration-700"></div>

                  <div className="relative z-10 space-y-8">
                    {/* Header Price */}
                    <div>
                      <div className="text-sm text-gray-600 mb-2 font-semibold">
                        Starting from
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-5xl font-bold text-gray-900 tracking-tight">
                          {formatCurrency(ticket?.PerPersonRate || 0)}
                        </span>
                        <span className="text-gray-500 font-medium whitespace-nowrap">
                          / person
                        </span>
                      </div>
                      {ticket?.LastEntry && (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-600 rounded-xl text-xs font-semibold border border-orange-100 mt-3">
                          <Clock className="w-4 h-4 text-orange-500 shrink-0" />
                          <span>Last Entry: {ticket.LastEntry}</span>
                        </div>
                      )}
                    </div>

                    {/* Detailed Pricing List */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Tag className="w-4 h-4 text-orange-500" />
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-[0.5]">
                          Ticket Pricing Options
                        </h3>
                      </div>
                      <div className="space-y-3">
                        {ticket?.PerPersonRate > 0 && (
                          <div className="flex justify-between items-center px-4 py-2 bg-gray-50 rounded-2xl border border-transparent hover:border-orange-200 transition-colors group/item">
                            <span className="text-base font-medium text-gray-800 group-hover/item:text-orange-600 transition-colors">
                              Per Person
                            </span>
                            <span className="text-xl font-bold text-gray-900">
                              {formatCurrency(ticket?.PerPersonRate || 0)}
                            </span>
                          </div>
                        )}
                        {ticket?.ChildSevenYrRate > 0 && (
                          <div className="flex justify-between items-center px-4 py-2 bg-gray-50 rounded-2xl border border-transparent hover:border-orange-200 transition-colors group/item">
                            <span className="text-base font-medium text-gray-800 group-hover/item:text-orange-600 transition-colors">
                              Child (3yr - 15yr)
                            </span>
                            <span className="text-xl font-bold text-gray-900">
                              {formatCurrency(ticket?.ChildSevenYrRate || 0)}
                            </span>
                          </div>
                        )}
                        {ticket?.ChildThreeYrRate > 0 && (
                          <div className="flex justify-between items-center px-4 py-2 bg-gray-50 rounded-2xl border border-transparent hover:border-orange-200 transition-colors group/item">
                            <span className="text-base font-medium text-gray-800 group-hover/item:text-orange-600 transition-colors">
                              Child (3yr - 6yr)
                            </span>
                            <span className="text-xl font-bold text-gray-900">
                              {formatCurrency(ticket?.ChildThreeYrRate || 0)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {ticket?.SOUTicketDetails &&
                      ticket?.SOUTicketDetails.length > 0 && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 mb-4">
                            <Tag className="w-4 h-4 text-orange-500" />
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-[0.5]">
                              Ticket Pricing Options
                            </h3>
                          </div>
                          <div className="space-y-3">
                            {ticket?.SOUTicketDetails.map(
                              (rate: any, index: number) => (
                                <div
                                  key={index}
                                  className="flex justify-between items-center px-4 py-2 bg-gray-50 rounded-2xl border border-transparent hover:border-orange-200 transition-colors group/item"
                                >
                                  <span className="text-base font-medium text-gray-800 group-hover/item:text-orange-600 transition-colors">
                                    {rate.Title}
                                  </span>
                                  <span className="text-xl font-bold text-gray-900">
                                    {formatCurrency(rate.Rate)}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {/* Booking Button */}
                    {ticket?.ID && (
                      <div className="pt-4">
                        <Link
                          href={`/cart?ticketId=${ticket?.ID}`}
                          className="flex items-center justify-center gap-3 w-full py-4 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 text-white font-bold rounded-2xl hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-500 hover:scale-[1.02] active:scale-[0.98] uppercase tracking-widest text-sm"
                        >
                          <span>Book Now</span>
                        </Link>
                      </div>
                    )}

                    {/* Contact Info */}
                    {PhoneNo && (
                      <div className="pt-8 border-t border-gray-100">
                        <div className="flex items-center gap-4 group cursor-pointer">
                          <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center transition-colors group-hover:bg-orange-500">
                            <Clock className="w-6 h-6 text-orange-600 transition-colors group-hover:text-white" />
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-0.5">
                              Need Help?
                            </p>
                            <Link
                              href={`tel:${PhoneNo}`}
                              className="text-xl font-bold text-gray-900 hover:text-orange-600 transition-colors underline decoration-orange-500/30 underline-offset-4"
                            >
                              {PhoneNo}
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </aside>
          </div>
        </div>
      </div>

      {/* Synchronized Image Modal with Slider */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex justify-between items-center px-4 sm:px-8 py-4 sm:py-6 text-white absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 via-black/30 to-transparent">
              <div className="flex items-center gap-4 sm:gap-6">
                <div className="border-l-4 border-orange-500 pl-4 sm:pl-6">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span>{ticket.Title}</span>
                    {activeTitle && (
                      <span className="text-orange-500 text-sm sm:text-lg font-semibold">
                        • {activeTitle}
                      </span>
                    )}
                  </h3>
                </div>
              </div>
              <button
                onClick={() => setSelectedIndex(null)}
                className="bg-white/10 hover:bg-orange-600 p-2.5 sm:p-3 rounded-full backdrop-blur-lg transition-all group border border-white/10 flex-shrink-0"
                aria-label="Close modal"
              >
                <X
                  className="text-white group-hover:rotate-90 transition-transform duration-500 w-5 h-5 sm:w-6 sm:h-6"
                />
              </button>
            </div>

            {/* Sliders Area */}
            <div className="flex-1 flex flex-col mt-20 justify-center items-center p-4 md:p-8 w-full max-w-[1400px] mx-auto overflow-hidden">
              {/* Main Slider */}
              <div className="w-full relative px-6 md:px-20 mb-8 max-h-[60vh]">
                <Slider
                  asNavFor={nav2!}
                  ref={slider1}
                  initialSlide={selectedIndex}
                  infinite={true}
                  speed={500}
                  slidesToShow={1}
                  slidesToScroll={1}
                  arrows={true}
                  fade={true}
                  nextArrow={<NextArrow />}
                  prevArrow={<PrevArrow />}
                  className="w-full gallery-main-slider"
                >
                  {activeImages.map((image, index) => (
                    <div
                      key={index}
                      className="relative outline-none flex justify-center"
                    >
                      <div className="relative w-full h-[65vh] md:h-[70vh]">
                        <Image
                          src={image}
                          alt="Ticket View"
                          fill
                          className="object-contain"
                          priority
                          sizes="100vw"
                        />
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>

              {/* Thumbnails Slider */}
              <div className="w-full max-w-xl px-6 h-20 md:h-24">
                <Slider
                  asNavFor={nav1!}
                  ref={slider2}
                  initialSlide={selectedIndex}
                  slidesToShow={
                    activeImages.length < 5 ? activeImages.length : 5
                  }
                  swipeToSlide={true}
                  focusOnSelect={true}
                  infinite={activeImages.length > 1}
                  centerMode={true}
                  centerPadding="0px"
                  variableWidth={true}
                  arrows={false}
                  className="thumb-nav-slider"
                  responsive={[
                    { breakpoint: 1024, settings: { slidesToShow: 5 } },
                    { breakpoint: 768, settings: { slidesToShow: 4 } },
                    { breakpoint: 480, settings: { slidesToShow: 3 } },
                  ]}
                >
                  {activeImages.map((image, index) => (
                    <div
                      key={index}
                      className="px-2 py-1 focus:outline-none cursor-pointer group !w-auto"
                    >
                      <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 border-transparent transition-all opacity-30 [.slick-current_&]:opacity-100 [.slick-current_&]:border-orange-500 [.slick-current_&]:scale-110 group-hover:opacity-70 group-hover:scale-105 shadow-sm">
                        <Image
                          src={image}
                          alt="Thumbnail"
                          fill
                          className="object-cover"
                          sizes="100px"
                        />
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Related Tickets Slider Section */}
      {ticket?.AnotherTicket && ticket?.AnotherTicket?.length > 0 && (
        <section className="py-20 bg-gray-50/50 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-1.5 rounded-full mb-4">
                <Ticket className="w-4 h-4" />
                <span className="font-bold tracking-wider uppercase text-xs">
                  Discover More
                </span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Explore Other Popular Tickets
              </h2>
            </motion.div>

            <div className="relative group">
              {isMounted ? (
                <Slider
                  dots={true}
                  infinite={ticket.AnotherTicket.length > slidesCount}
                  speed={800}
                  slidesToShow={slidesCount}
                  slidesToScroll={1}
                  arrows={true}
                  prevArrow={<RelatedTicketPrevArrow />}
                  nextArrow={<RelatedTicketNextArrow />}
                  autoplay={true}
                  autoplaySpeed={5000}
                  className="another-tickets-slider !-mx-4 px-2 md:px-0"
                >
                  {ticket?.AnotherTicket?.map((item: any, index: number) => (
                    <div key={index} className="px-4 py-8 h-full">
                      <TicketCard ticket={item} index={index} />
                    </div>
                  ))}
                </Slider>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 !-mx-4 px-2 md:px-0 py-8">
                  {ticket?.AnotherTicket?.slice(0, 3).map((item: any, index: number) => (
                    <div
                      key={index}
                      className={`h-full ${
                        index > 0 ? (index === 1 ? "hidden md:block lg:block" : "hidden lg:block") : "block"
                      }`}
                    >
                      <TicketCard ticket={item} index={index} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Synchronized Video Modal with Slider */}
      <AnimatePresence>
        {selectedVideoIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex justify-between items-center px-4 sm:px-8 py-4 sm:py-6 text-white absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 via-black/30 to-transparent">
              <div className="flex items-center gap-4 sm:gap-6">
                <div className="border-l-4 border-orange-500 pl-4 sm:pl-6">
                  <h3 className="text-lg md:text-2xl font-bold tracking-tight">
                    {ticket.Title}
                    <span className="text-orange-500"> - Video Gallery</span>
                  </h3>
                </div>
              </div>
              <button
                onClick={() => setSelectedVideoIndex(null)}
                className="bg-white/10 hover:bg-orange-600 p-2.5 sm:p-3 rounded-full backdrop-blur-lg transition-all group border border-white/10 flex-shrink-0"
                aria-label="Close modal"
              >
                <X
                  className="text-white group-hover:rotate-90 transition-transform duration-500 w-5 h-5 sm:w-6 sm:h-6"
                />
              </button>
            </div>

            {/* Sliders Area */}
            <div className="flex-1 flex flex-col mt-20 justify-center items-center p-2 md:p-8 w-full max-w-[1400px] mx-auto overflow-hidden">
              {/* Main Slider */}
              <div className="w-full relative px-2 md:px-20 mb-8">
                <Slider
                  asNavFor={navVideo2!}
                  ref={sliderVideo1}
                  initialSlide={selectedVideoIndex}
                  infinite={true}
                  speed={500}
                  slidesToShow={1}
                  slidesToScroll={1}
                  arrows={true}
                  fade={true}
                  nextArrow={<NextArrow />}
                  prevArrow={<PrevArrow />}
                  beforeChange={(_, next) => setCurrentVideoSlide(next)}
                  className="w-full gallery-main-slider"
                >
                  {ticket?.SOUTicketVideo?.map((video: any, index: number) => {
                    const videoID = getYouTubeID(video.URL);
                    return (
                      <div
                        key={index}
                        className="relative outline-none flex justify-center"
                      >
                        <div className="relative w-full aspect-video md:h-[65vh] rounded-xl md:rounded-3xl overflow-hidden bg-black shadow-2xl">
                          {currentVideoSlide === index && (
                            <iframe
                              src={`https://www.youtube.com/embed/${videoID}?autoplay=1&rel=0`}
                              className="w-full h-full border-0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          )}
                          {currentVideoSlide !== index && (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image
                                src={`https://img.youtube.com/vi/${videoID}/maxresdefault.jpg`}
                                alt="Video Thumbnail"
                                fill
                                className="object-cover opacity-50"
                              />
                              <Play className="w-20 h-20 text-white/50" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Slider>
              </div>

              {/* Thumbnails Slider */}
              <div className="w-full max-w-xl px-6 h-20 md:h-24">
                <Slider
                  asNavFor={navVideo1!}
                  ref={sliderVideo2}
                  initialSlide={selectedVideoIndex}
                  slidesToShow={
                    ticket?.SOUTicketVideo?.length < 5
                      ? ticket?.SOUTicketVideo?.length
                      : 5
                  }
                  swipeToSlide={true}
                  focusOnSelect={true}
                  infinite={ticket?.SOUTicketVideo?.length > 1}
                  centerMode={true}
                  centerPadding="0px"
                  variableWidth={true}
                  arrows={false}
                  className="thumb-nav-slider"
                  responsive={[
                    { breakpoint: 1024, settings: { slidesToShow: 5 } },
                    { breakpoint: 768, settings: { slidesToShow: 4 } },
                    { breakpoint: 480, settings: { slidesToShow: 3 } },
                  ]}
                >
                  {ticket?.SOUTicketVideo?.map((video: any, index: number) => {
                    const videoID = getYouTubeID(video.URL);
                    return (
                      <div
                        key={index}
                        className="px-2 py-1 focus:outline-none cursor-pointer group !w-auto"
                      >
                        <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 border-transparent transition-all opacity-30 [.slick-current_&]:opacity-100 [.slick-current_&]:border-orange-500 [.slick-current_&]:scale-110 group-hover:opacity-70 group-hover:scale-105 shadow-sm">
                          <Image
                            src={`https://img.youtube.com/vi/${videoID}/default.jpg`}
                            alt="Thumbnail"
                            fill
                            className="object-cover"
                            sizes="100px"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Play className="w-6 h-6 text-white opacity-80" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Slider>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx global>{`
        .gallery-main-slider .slick-slide {
          padding: 0 10px;
        }
        .thumb-nav-slider .slick-track {
          display: flex !important;
          align-items: center !important;
        }
      `}</style>
    </>
  );
}

// Custom Navigation Arrows for Slick Slider
function NextArrow({ onClick }: any) {
  return (
    <button
      onClick={onClick}
      className="absolute right-[-20px] md:right-[-40px] top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-orange-600 p-3 rounded-full text-white backdrop-blur-xl transition-all border border-white/10 flex items-center justify-center group"
    >
      <ChevronRight
        size={30}
        className="group-hover:translate-x-1 transition-transform"
      />
    </button>
  );
}

function PrevArrow({ onClick }: any) {
  return (
    <button
      onClick={onClick}
      className="absolute left-[-20px] md:left-[-40px] top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-orange-600 p-3 rounded-full text-white backdrop-blur-xl transition-all border border-white/10 flex items-center justify-center group"
    >
      <ChevronLeft
        size={30}
        className="group-hover:-translate-x-1 transition-transform"
      />
    </button>
  );
}
